import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Truck, Percent, Sparkles } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1599462616558-2b75fd26a283";
const CATEGORY_IMG = {
  clothing: "https://images.unsplash.com/photo-1587538018365-2a1f8b544c08",
  electronics: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?crop=entropy&cs=srgb&fm=jpg&q=85",
  grocery: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?crop=entropy&cs=srgb&fm=jpg&q=85",
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [p, c] = await Promise.all([
          api.get("/products?limit=8&sort=newest"),
          api.get("/categories"),
        ]);
        setFeatured(p.data);
        setCategories(c.data);
      } catch {}
    })();
  }, []);

  return (
    <div>
      {/* Hero Bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-14">
        <div className="grid md:grid-cols-12 gap-4 md:gap-6">
          <div className="md:col-span-8 relative rounded-2xl overflow-hidden bg-[#0F3D2E] min-h-[380px] md:min-h-[500px]" data-testid="hero-main">
            <img src={HERO_IMG} alt="Florvanta hero" className="absolute inset-0 w-full h-full object-cover opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F3D2E]/85 via-[#0F3D2E]/60 to-transparent" />
            <div className="relative z-10 p-8 md:p-14 flex flex-col justify-end h-full min-h-[380px] md:min-h-[500px]">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full px-3 py-1 mb-4 w-fit border border-white/20">
                <Sparkles size={14} className="text-[#14A085]" />
                <span className="text-white text-xs tracking-widest uppercase">Only 1% Commission</span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white font-medium leading-none">
                India's fairest<br />
                <span className="text-[#14A085]">multi-vendor</span> marketplace.
              </h1>
              <p className="mt-4 max-w-lg text-white/80 text-base">
                From artisan clothing to trending electronics to fresh grocery — one login, thousands of Indian sellers, PAN-India delivery.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/products" data-testid="hero-shop-btn">
                  <Button className="bg-[#14A085] hover:bg-white hover:text-[#0F3D2E] text-white rounded-full px-6 pill-btn">
                    Shop Now <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/sell" data-testid="hero-sell-btn">
                  <Button variant="outline" className="border-white text-white bg-transparent hover:bg-white hover:text-[#0F3D2E] rounded-full px-6">
                    Become a Seller
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-4 md:gap-6">
            <div className="relative rounded-2xl overflow-hidden bg-[#E8F5F2] min-h-[180px] md:min-h-[240px] group" data-testid="hero-tile-clothing">
              <img src={CATEGORY_IMG.clothing} alt="Clothing" className="absolute inset-0 w-full h-full object-cover" />
              <Link to="/products?category=clothing" className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex flex-col justify-end p-5">
                <div className="text-white text-xs uppercase tracking-[0.2em]">Category</div>
                <div className="text-white font-display text-2xl">Clothing</div>
              </Link>
            </div>
            <div className="relative rounded-2xl overflow-hidden bg-[#0F3D2E] min-h-[180px] md:min-h-[240px]" data-testid="hero-tile-commission">
              <div className="absolute inset-0 grain-bg" />
              <div className="relative p-6 h-full flex flex-col justify-between">
                <Percent size={28} className="text-[#14A085]" />
                <div>
                  <div className="text-[#14A085] text-xs uppercase tracking-[0.2em]">Sellers keep</div>
                  <div className="text-white font-display text-4xl">99%</div>
                  <div className="text-white/70 text-sm mt-1">Just 1% commission on sales</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Explore</div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#0F3D2E]">Shop by category</h2>
          </div>
          <Link to="/products" className="text-sm text-[#14A085] hover:underline">View all →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {categories.map((c) => (
            <Link key={c.slug} to={`/products?category=${c.slug}`} className="card-hover relative rounded-xl overflow-hidden border border-[#E2E8E5] bg-white" data-testid={`category-tile-${c.slug}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={CATEGORY_IMG[c.slug]} alt={c.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-5 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Category</div>
                  <div className="font-display text-2xl text-[#0F3D2E]">{c.name}</div>
                </div>
                <ArrowRight size={22} className="text-[#0F3D2E]" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Value props */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Percent, title: "Just 1% Commission", desc: "Lowest fees in India. Sellers keep 99% of every sale." },
            { icon: Truck, title: "PAN-India Shipping", desc: "Delivered coast to coast with tracked shipments." },
            { icon: ShieldCheck, title: "Verified Vendors", desc: "Every seller passes KYC before going live." },
          ].map((v, i) => (
            <div key={i} className="rounded-xl border border-[#E2E8E5] bg-white p-6" data-testid={`value-prop-${i}`}>
              <v.icon size={22} className="text-[#14A085]" />
              <div className="mt-3 font-display text-xl text-[#0F3D2E]">{v.title}</div>
              <p className="text-sm text-[#3B5249] mt-1">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Trending</div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#0F3D2E]">Fresh from our vendors</h2>
          </div>
          <Link to="/products" className="text-sm text-[#14A085] hover:underline" data-testid="see-all-products">
            See all products →
          </Link>
        </div>
        {featured.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#E2E8E5] bg-white p-10 text-center">
            <p className="text-[#3B5249]">
              No products yet. <Link to="/sell" className="text-[#14A085] underline">Become a seller</Link> and be the first to list!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* Seller CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="relative rounded-2xl overflow-hidden bg-[#0F3D2E] p-8 md:p-14">
          <div className="absolute inset-0 grain-bg" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">For businesses</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mt-2 font-medium">
                Sell across India.<br />Keep 99% of every rupee.
              </h2>
              <p className="text-white/80 mt-4 max-w-md">
                Onboard in minutes, list unlimited products, and get paid weekly. We take just 1% — no listing fees, no monthly charges.
              </p>
              <div className="mt-6">
                <Link to="/sell" data-testid="cta-become-seller">
                  <Button className="bg-[#14A085] hover:bg-white hover:text-[#0F3D2E] text-white rounded-full px-6 pill-btn">
                    Start Selling <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[["1%","Commission"],["₹0","Listing fees"],["PAN","India reach"]].map(([n,l],i) => (
                <div key={i} className="rounded-xl bg-white/5 border border-white/10 p-4 text-center">
                  <div className="font-display text-3xl text-[#14A085]">{n}</div>
                  <div className="text-xs text-white/70 uppercase tracking-widest mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
